import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp, FirebaseApp, FirebaseOptions } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, signInWithCustomToken, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';

// Firebase context
export const FirebaseContext = createContext<{
  app: FirebaseApp | null;
  auth: Auth | null;
  db: Firestore | null;
  userId: string;
  isAuthReady: boolean;
  useLocalStorage: boolean;
} | null>(null);

// Custom hook to use Firebase context
export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

interface CustomWindow extends Window {
  __firebase_config?: FirebaseOptions;
  __initial_auth_token?: string;
}

// Firebase provider component
export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [userId, setUserId] = useState('');
  const [useLocalStorage, setUseLocalStorage] = useState(false);
  const [app, setApp] = useState<FirebaseApp | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [db, setDb] = useState<Firestore | null>(null);

  useEffect(() => {
    let firebaseApp: FirebaseApp | null = null;
    let firebaseAuth: Auth | null = null;
    let firebaseDb: Firestore | null = null;

    // Check for global Firebase config or environment variables
    const firebaseConfig = (window as CustomWindow).__firebase_config || {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
    };

    // Check if config is valid (has at least an apiKey)
    if (firebaseConfig && firebaseConfig.apiKey && firebaseConfig.apiKey !== 'your_api_key_here') {
      try {
        // Initialize Firebase
        firebaseApp = initializeApp(firebaseConfig);
        firebaseAuth = getAuth(firebaseApp);
        firebaseDb = getFirestore(firebaseApp);
        
        setApp(firebaseApp);
        setAuth(firebaseAuth);
        setDb(firebaseDb);

        // Try to sign in
        const initializeAuth = async () => {
          try {
            // Check for custom token first
            const customToken = (window as CustomWindow).__initial_auth_token;
            if (customToken && firebaseAuth) {
              await signInWithCustomToken(firebaseAuth, customToken);
            } else {
              if (firebaseAuth) {
                await signInAnonymously(firebaseAuth);
              }
            }
          } catch (error) {
            console.error('Firebase auth error:', error);
            setUseLocalStorage(true);
          }
        };

        // Listen for auth state changes
        if (firebaseAuth) {
          onAuthStateChanged(firebaseAuth, (user) => {
            if (user) {
              setUserId(user.uid);
              setUseLocalStorage(false); // Ensure we are in Online Mode
            } else {
              // Fallback: Generate a UUID for localStorage (persisted)
              const storedOfflineId = localStorage.getItem('sworn_offline_uid');
              let fallbackUserId;
              if (storedOfflineId) {
                  fallbackUserId = storedOfflineId;
              } else {
                  fallbackUserId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9);
                  localStorage.setItem('sworn_offline_uid', fallbackUserId);
              }
              setUserId(fallbackUserId);
              setUseLocalStorage(true);
            }
            setIsAuthReady(true);
          });
        }

        initializeAuth();
      } catch (error) {
        console.error('Firebase initialization error:', error);
        setUseLocalStorage(true);
        // Fallback: Check local storage first so we persist the ID across refreshes
        const storedOfflineId = localStorage.getItem('sworn_offline_uid');
        let fallbackUserId;
        if (storedOfflineId) {
            fallbackUserId = storedOfflineId;
        } else {
            fallbackUserId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9);
            localStorage.setItem('sworn_offline_uid', fallbackUserId);
        }
        setUserId(fallbackUserId);
        setIsAuthReady(true);
      }
    } else {
      setUseLocalStorage(true);
      // Fallback: Generate a UUID for localStorage
      const fallbackUserId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9);
      setUserId(fallbackUserId);
      setIsAuthReady(true);
    }

    // Cleanup function
    return () => {
      if (firebaseApp) {
        // Firebase cleanup if needed
      }
    };
  }, []);

  const value = {
    app,
    auth,
    db,
    userId,
    isAuthReady,
    useLocalStorage,
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};
